// src/components/ImageZoomer.jsx (path as per your structure)
import React, { useRef, useEffect } from "react";
import styled from "styled-components";

const MainContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: zoom-in;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OriginalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const MagnifyingLens = styled.div`
  position: absolute;
  width: 120px;
  height: 120px;
  background-color: rgba(200, 160, 84, 0.15); /* secondary color matching theme */
  border: 1px solid #C8A054;
  pointer-events: none;
  opacity: 0;
  border-radius: 4px;
  z-index: 10;
  transform: translate(-50%, -50%);
`;

const ZoomPortalPanel = styled.div`
  position: fixed;
  background-color: #ffffff;
  box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  pointer-events: none;
  overflow: hidden;
  z-index: 2000;
  display: none;
  background-repeat: no-repeat;
  transition: opacity 0.2s ease;
`;

const ImageZoomer = ({ src, alt, zoomSrc, ...imgProps }) => {
  const containerRef = useRef(null);
  const lensRef = useRef(null);
  const originalImgRef = useRef(null);
  const zoomPanelRef = useRef(null);

  useEffect(() => {
    // Append the ZoomPanel outside the current container to body to avoid overflow cuts
    const panel = document.createElement("div");
    zoomPanelRef.current = panel;
    document.body.appendChild(panel);

    // Apply Styles to Portal Panel directly
    const s = panel.style;
    s.position = "fixed";
    s.backgroundColor = "#fff";
    s.boxShadow = "0 12px 45px rgba(0,0,0,0.15)";
    s.border = "1px solid #eaeaea";
    s.borderRadius = "12px";
    s.pointerEvents = "none";
    s.overflow = "hidden";
    s.zIndex = "2500";
    s.display = "none";
    s.backgroundRepeat = "no-repeat";
    s.backgroundImage = `url(${zoomSrc || src})`;

    return () => {
      document.body.removeChild(panel);
    };
  }, [src, zoomSrc]);

  const handleMouseMove = (e) => {
    if (!containerRef.current || !lensRef.current || !zoomPanelRef.current) return;

    // Only activate Side panel Zoom for larger screens (similar to Flipkart/Amazon)
    if (window.innerWidth < 768) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Math calculation for boundary lock
    const lens = lensRef.current;
    const lensWidth = lens.offsetWidth;
    const lensHeight = lens.offsetHeight;

    let moveX = x;
    let moveY = y;

    if (moveX < lensWidth / 2) moveX = lensWidth / 2;
    if (moveX > rect.width - lensWidth / 2) moveX = rect.width - lensWidth / 2;
    if (moveY < lensHeight / 2) moveY = lensHeight / 2;
    if (moveY > rect.height - lensHeight / 2) moveY = rect.height - lensHeight / 2;

    lens.style.left = `${moveX}px`;
    lens.style.top = `${moveY}px`;

    // Zoom multiplier
    const ratio = 2.4; // Zoom level multipliers
    const panel = zoomPanelRef.current;

    // Set panel size to match viewport nicely
    panel.style.width = `${rect.width}px`;
    panel.style.height = `${rect.height}px`;

    // Position float panel absolutely matching original bounds offset right
    panel.style.top = `${rect.top}px`;
    panel.style.left = `${rect.left + rect.width + 20}px`;

    // Calculate background alignment offset position
    panel.style.backgroundSize = `${rect.width * ratio}px ${rect.height * ratio}px`;
    panel.style.backgroundPosition = `-${moveX * ratio - rect.width / 2}px -${moveY * ratio - rect.height / 2}px`;
  };

  const handleMouseEnter = () => {
    if (window.innerWidth < 768) return;
    if (lensRef.current) lensRef.current.style.opacity = "1";
    if (zoomPanelRef.current) zoomPanelRef.current.style.display = "block";
  };

  const handleMouseLeave = () => {
    if (lensRef.current) lensRef.current.style.opacity = "0";
    if (zoomPanelRef.current) zoomPanelRef.current.style.display = "none";
  };

  return (
    <MainContainer
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MagnifyingLens ref={lensRef} />
      <OriginalImage ref={originalImgRef} alt={alt} src={src} {...imgProps} />
    </MainContainer>
  );
};

export default ImageZoomer;
